import type { Coding } from '@medplum/fhirtypes'
import type { CodingAnswerOption } from '#src/codings.ts'
import { ASSESSMENT_ANSWER_CODES } from '#src/terminology/answerCodes.ts'
import { answerSetOptions } from '#src/terminology/answerSetOptions.ts'
import { ANSWER_SETS } from '#src/terminology/answerSets.ts'
import { AD8_TEMPLATE } from '#src/terminology/instruments/ad8.ts'
import { ADL_TEMPLATE } from '#src/terminology/instruments/adl.ts'
import { BRADEN_TEMPLATE } from '#src/terminology/instruments/braden.ts'
import { GAD2_TEMPLATE } from '#src/terminology/instruments/gad2.ts'
import { GAD7_TEMPLATE } from '#src/terminology/instruments/gad7.ts'
import { IADL_TEMPLATE } from '#src/terminology/instruments/iadl.ts'
import { MINI_COG_TEMPLATE } from '#src/terminology/instruments/miniCog.ts'
import { MMSE_TEMPLATE } from '#src/terminology/instruments/mmse.ts'
import { MOCA_TEMPLATE } from '#src/terminology/instruments/moca.ts'
import { PHQ2_TEMPLATE } from '#src/terminology/instruments/phq2.ts'
import { PHQ9_TEMPLATE } from '#src/terminology/instruments/phq9.ts'
import { SLUMS_TEMPLATE } from '#src/terminology/instruments/slums.ts'
import { STEADI_TEMPLATE } from '#src/terminology/instruments/steadi.ts'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '#src/terminology/instruments/types.ts'
import { ASSESSMENT_QUESTION_CODES } from '#src/terminology/questionCodes.ts'
import { STANDARD_CODE_MAP } from '#src/terminology/standardCodeMap.ts'
import type { TerminologyLibrary } from '#src/terminology/types.ts'

// Read from the terminology modules directly rather than through
// `TERMINOLOGY_LIBRARY`, which would make this registry and the library
// import each other.
const LIBRARY: TerminologyLibrary = {
  questionCodes: ASSESSMENT_QUESTION_CODES,
  answerCodes: ASSESSMENT_ANSWER_CODES,
  answerSets: ANSWER_SETS,
  conceptMap: STANDARD_CODE_MAP,
}

// Every instrument in source, by id. Templates are added as they are
// ported.
export const INSTRUMENT_TEMPLATES: InstrumentTemplate[] = [
  PHQ9_TEMPLATE,
  GAD7_TEMPLATE,
  PHQ2_TEMPLATE,
  GAD2_TEMPLATE,
  STEADI_TEMPLATE,
  AD8_TEMPLATE,
  BRADEN_TEMPLATE,
  MINI_COG_TEMPLATE,
  ADL_TEMPLATE,
  IADL_TEMPLATE,
  MOCA_TEMPLATE,
  SLUMS_TEMPLATE,
  MMSE_TEMPLATE,
]

export function instrumentTemplate(
  id: string | null | undefined,
  templates: readonly InstrumentTemplate[] = INSTRUMENT_TEMPLATES
): InstrumentTemplate | undefined {
  return templates.find((template) => template.id === id)
}

// Every item, in order, sections flattened.
export function instrumentItems(
  template: InstrumentTemplate
): InstrumentItem[] {
  return template.sections.flatMap((section) => section.items)
}

export function instrumentItem(
  template: InstrumentTemplate,
  coding: Pick<Coding, 'system' | 'code'>
): InstrumentItem | undefined {
  return instrumentItems(template).find(
    (item) =>
      item.coding.code === coding.code &&
      (!item.coding.system ||
        !coding.system ||
        item.coding.system === coding.system)
  )
}

// The answers an item offers, scores included: its own list, else its
// value set's concepts.
export function instrumentAnswerOptions(
  item: InstrumentItem,
  library: TerminologyLibrary = LIBRARY
): CodingAnswerOption[] {
  if (item.answerOptions) {
    return item.answerOptions
  }
  if (!item.answerSet) {
    return []
  }
  return answerSetOptions(library, item.answerSet).map(({ coding, score }) => {
    const option: CodingAnswerOption = {
      code: coding.code ?? '',
      display: coding.display ?? coding.code ?? '',
    }
    if (coding.system) {
      option.system = coding.system
    }
    if (score !== undefined) {
      option.score = score
    }
    return option
  })
}

// What an instrument is called for short: its short name, else the acronym
// its title ends with, as in "(PHQ-9)", else its title.
export function instrumentShortName(template: InstrumentTemplate): string {
  return (
    template.shortName ??
    template.title.match(/\(([^)]+)\)\s*$/)?.[1] ??
    template.title
  )
}
